import type { InjectionKey, Ref } from "vue";
import { inject, provide, ref } from "vue";
import {
	fetchMasterFortuneMobilePageData,
	fetchMasterFortunePcPageData,
	type MasterFortuneMobilePageData,
	type MasterFortunePcPageData,
} from "~/utils/page-data/master-fortune";

const MasterFortunePcPageDataKey: InjectionKey<Ref<MasterFortunePcPageData | null>> =
	Symbol("masterFortunePcPageData");
const MasterFortuneMobilePageDataKey: InjectionKey<Ref<MasterFortuneMobilePageData | null>> =
	Symbol("masterFortuneMobilePageData");

export function useMasterFortunePageData(
	platform: "pc" | "mobile",
	options?: { sortType?: string }
) {
	const dataKey =
		platform === "pc" ? "master-fortune-pc-index-data" : "master-fortune-mobile-index-data";

	const { data, refresh, pending, error } = useLazyAsyncData<
		MasterFortunePcPageData | MasterFortuneMobilePageData
	>(
		dataKey,
		() =>
			platform === "pc"
				? fetchMasterFortunePcPageData()
				: fetchMasterFortuneMobilePageData(options?.sortType),
		{
			default: () =>
				platform === "pc"
					? ({
							evaluateList: [],
							rankingData: { dongfang: [], tarot: [] },
							resourceTeachers: [],
							courseList: [],
							coursePager: {},
						} satisfies MasterFortunePcPageData)
					: ({
							mentorList: [],
							mentorPager: {},
							lectureList: [],
							lecturePager: {},
							evaluateList: [],
							resourceTeachers: [],
						} satisfies MasterFortuneMobilePageData),
			server: true,
		}
	);

	if (platform === "pc") {
		provide(MasterFortunePcPageDataKey, data as Ref<MasterFortunePcPageData | null>);
	} else {
		provide(MasterFortuneMobilePageDataKey, data as Ref<MasterFortuneMobilePageData | null>);
	}

	return {
		data,
		refresh,
		pending,
		error,
	};
}

export function useMasterFortunePcPageData() {
	return inject(MasterFortunePcPageDataKey, ref(null));
}

export function useMasterFortuneMobilePageData() {
	return inject(MasterFortuneMobilePageDataKey, ref(null));
}
